import { isAxiosError } from 'axios';

import { getRetryingAxiosInstance } from '@smg-real-estate/hg-search-libs';

export type RestageTransformation = 'REMOVE_FURNITURE' | 'CHANGE_STYLE';

export interface RestageRequestPayload {
  imageUrl: string;
  transformation: RestageTransformation;
  objectReference: string;
  fileName?: string;
}

export interface RestageResponsePayload {
  cdnUrl: string;
}

interface RestageErrorResponse {
  code?: string;
}

const restageImageEndpoint = `${import.meta.env.VUE_INSERTION_FUNNEL_API_ENDPOINT}/restage/image`;

const axiosInstance = getRetryingAxiosInstance({
  timeout: 45000, // LLM image generation may take a while
  retryConfig: {
    retries: 3,
  },
});

function extractResponseCode(responseData: unknown): string | undefined {
  if (!responseData || typeof responseData !== 'object' || !('code' in responseData)) {
    return undefined;
  }

  const { code } = responseData as RestageErrorResponse;
  return code || undefined;
}

function getAxiosErrorType(error: unknown): string | undefined {
  if (!isAxiosError(error)) {
    return undefined;
  }

  const responseCode = extractResponseCode(error.response?.data);
  if (responseCode) {
    return responseCode;
  }

  if (error.code === 'ECONNABORTED') {
    return 'timeout';
  }

  if (!error.response) {
    return 'network';
  }

  return error.response?.status ? `http_${error.response.status}` : undefined;
}

export function getRestageErrorType(error: unknown): string {
  const axiosErrorType = getAxiosErrorType(error);
  if (axiosErrorType) {
    return axiosErrorType;
  }

  if (error instanceof Error && error.message.toLowerCase().includes('timeout')) {
    return 'timeout';
  }

  return 'unknown';
}

export async function postRestageImage(
  payload: RestageRequestPayload,
): Promise<RestageResponsePayload> {
  const response = await axiosInstance.post<RestageResponsePayload>(restageImageEndpoint, payload);
  return response.data;
}
